// ─────────────────────────────────────────────
// AcceptableUsePolicy.tsx
// ─────────────────────────────────────────────
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'legal@autopilot.co.zw';
const lastUpdated = '1 January 2025';

const aupSections = [
  {
    id: 'purpose',
    title: '1. Purpose',
    content: `This Acceptable Use Policy ("AUP") governs what you may and may not do with the ${companyName} platform. It exists to protect ${companyName}, our users, End Users, and the public from harm. Violation of this AUP may result in immediate suspension or termination of your account without refund.

This AUP is incorporated into and forms part of our Terms of Service.`,
  },
  {
    id: 'permitted',
    title: '2. Permitted Uses',
    content: `You may use the Platform to:
• Create and operate chatbots that respond to genuine customer enquiries about your legitimate business.
• Automate appointment bookings, order taking, FAQs, and lead collection for your own business.
• Send transactional messages to customers who have explicitly initiated contact or opted in.
• Provide customer support and information services related to your products and services.
• Collect customer contact information with the customer's knowledge and consent.`,
  },
  {
    id: 'prohibited-messaging',
    title: '3. Prohibited Messaging Practices',
    content: `You must never use the Platform to send:

**3.1 Spam and unsolicited messages**
• Bulk unsolicited messages to people who have not opted in to receive them.
• Messages sent using purchased, scraped, or rented contact lists.
• Messages disguised as personal communications when they are commercial.

**3.2 Deceptive content**
• Phishing messages designed to steal credentials or financial information.
• Messages impersonating another business, individual, or government entity.
• False claims about products, services, prices, or your identity.
• "Get rich quick" schemes, pyramid schemes, or multi-level marketing recruitment messages.

**3.3 Harmful content**
• Content that promotes, glorifies, or facilitates violence, terrorism, or self-harm.
• Content that is sexually explicit, obscene, or directed at minors.
• Content designed to harass, threaten, or abuse individuals.
• Content that promotes hate based on race, ethnicity, religion, gender, sexual orientation, disability, or national origin.

**3.4 Illegal content**
• Anything prohibited under Zimbabwean law or the laws of the recipient's jurisdiction.
• Promotion or facilitation of drug trafficking, human trafficking, or other illegal trade.
• Unlicensed financial services, investment solicitation, or gambling services.`,
  },
  {
    id: 'prohibited-platform',
    title: '4. Prohibited Platform Activities',
    content: `You must not:

**4.1 Misuse of credentials and access**
• Share your API credentials, access tokens, or account access with unauthorised parties.
• Use access tokens belonging to another person or business without their authorisation.
• Attempt to access other users' bots, data, or accounts.

**4.2 Technical abuse**
• Attempt to reverse-engineer, decompile, or extract source code from the Platform.
• Use automated tools (scripts, bots) to create accounts, place orders, or otherwise abuse the Platform.
• Introduce malware, viruses, or malicious code into the Platform.
• Conduct load testing, denial-of-service attacks, or any action that impairs the Platform's performance.
• Circumvent usage limits, rate limits, or bot conversation caps.

**4.3 False information**
• Register an account using false identity or business details.
• Submit fraudulent or forged documents during WhatsApp Business verification.
• Represent yourself as a ${companyName} employee or authorised representative.

**4.4 Reselling without authorisation**
• Resell or sublicence access to the Platform without a written reseller agreement with ${companyName}.
• White-label the Platform for use by third parties unless you hold an Agency plan with appropriate terms.`,
  },
  {
    id: 'meta-compliance',
    title: '5. Meta Platform Compliance',
    content: `Because our Platform relies on WhatsApp Business API and Facebook Messenger (both operated by Meta Platforms, Inc.), you must also comply with:
• Meta's Messaging Policy;
• WhatsApp Business Policy;
• Meta Platform Terms;
• Meta's Community Standards.

Key requirements include:
• You must have a lawful basis and/or explicit opt-in consent before messaging End Users proactively on WhatsApp.
• WhatsApp Business API may only be used for business-to-consumer communications related to an existing customer relationship or an explicit opt-in.
• You must honour opt-out requests immediately. If a user sends "STOP" or asks not to be contacted, your bot must cease messaging them.
• You must not use WhatsApp or Facebook Messenger for political campaigning, unsolicited fundraising, or promoting restricted industries (alcohol, gambling, etc.) without Meta's explicit permission.

Breaches of Meta's Policies can result in your WhatsApp number or Facebook Page being permanently banned by Meta. ${companyName} is not responsible for such bans.`,
  },
  {
    id: 'data-collection',
    title: '6. End User Data Collection',
    content: `When your bot collects personal data from End Users, you must:

• Inform End Users clearly what data is being collected and why, at or before the point of collection.
• Have a lawful basis (consent, legitimate interest, contract) for each category of data collected.
• Only collect data that is necessary for the stated purpose (data minimisation).
• Not use data collected via bots for purposes beyond what the End User was informed about.
• Not sell End User data to third parties.
• Maintain a publicly accessible privacy notice that covers your bot's data practices.
• Comply with Zimbabwe's Cyber and Data Protection Act and, where your End Users are located in the EU/UK, the GDPR/UK GDPR.

${companyName} is a data processor acting on your instructions. You are the data controller. The legal responsibility for your End Users' data protection rights rests with you.`,
  },
  {
    id: 'enforcement',
    title: '7. Enforcement',
    content: `${companyName} reserves the right to:

• Monitor use of the Platform for AUP violations, including automated scanning of bot activity.
• Suspend or terminate accounts found to be in violation, immediately and without notice in serious cases.
• Report violations to Meta, relevant law enforcement, or regulatory authorities.
• Cooperate fully with law enforcement investigations.
• Seek injunctive relief or other legal remedies for violations that cause harm.

If you believe another Operator is violating this AUP, please report it to ${companyEmail} with the subject line "AUP Report".

If your account has been suspended and you believe it was in error, contact ${companyEmail} within 7 days with the subject line "AUP Suspension Appeal".`,
  },
];

function renderContent(content: string) {
  return content.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**')) {
      return <p key={i} style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '14px', marginBottom: '8px', marginTop: i > 0 ? '20px' : 0 }}>{line.replace(/\*\*/g, '')}</p>;
    }
    if (line.includes('**')) {
      const parts = line.split(/\*\*(.*?)\*\*/g);
      return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '8px' }}>{parts.map((part, j) => j % 2 === 1 ? <strong key={j} style={{ color: '#e2e8f0' }}>{part}</strong> : part)}</p>;
    }
    if (line.startsWith('• ')) return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '6px', paddingLeft: '16px', borderLeft: '2px solid #1e2330' }}>{line.slice(2)}</p>;
    if (line.trim() === '') return <div key={i} style={{ height: '8px' }} />;
    return <p key={i} style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.8', marginBottom: '8px' }}>{line}</p>;
  });
}

const policyStyles = `
  * { box-sizing: border-box; }
  .policy-nav-item { transition: all 0.15s; cursor: pointer; }
  .policy-nav-item:hover { background: rgba(91,79,232,0.1); }
  @media (max-width: 768px) { .policy-layout { flex-direction: column !important; } .policy-sidebar { width: 100% !important; position: static !important; border-right: none !important; border-bottom: 1px solid #1e2330 !important; } }
`;

export function AcceptableUsePolicy() {
  const [activeSection, setActiveSection] = useState('purpose');
  return (
    <>
      <SEO title={`Acceptable Use Policy | ${companyName}`} description={`What you can and cannot do on the ${companyName} platform.`} keywords="acceptable use policy, AUP, platform rules" type="website" />
      <style>{policyStyles}</style>
      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0f1117', color: 'white', minHeight: '100vh' }}>
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '60px', background: 'rgba(15,17,23,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1e2330', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Privacy</Link>
            <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Terms</Link>
            <Link to="/cookies" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Cookies</Link>
          </div>
        </nav>
        <div style={{ paddingTop: '100px', paddingBottom: '40px', paddingLeft: '32px', paddingRight: '32px', borderBottom: '1px solid #1e2330' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '16px' }}>LEGAL</div>
            <h1 style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '8px' }}>Acceptable Use Policy</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Last updated: {lastUpdated}</p>
          </div>
        </div>
        <div className="policy-layout" style={{ display: 'flex', maxWidth: '1100px', margin: '0 auto' }}>
          <div className="policy-sidebar" style={{ width: '260px', flexShrink: 0, borderRight: '1px solid #1e2330', padding: '24px 0', position: 'sticky', top: '60px', height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            <div style={{ padding: '0 16px', marginBottom: '12px', fontSize: '10px', fontWeight: '700', color: '#334155', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Sections</div>
            {aupSections.map(s => (
              <div key={s.id} className={`policy-nav-item ${activeSection === s.id ? 'active' : ''}`} onClick={() => { setActiveSection(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                style={{ padding: '9px 20px', fontSize: '13px', color: activeSection === s.id ? '#fca5a5' : '#64748b', borderLeft: activeSection === s.id ? '3px solid #ef4444' : '3px solid transparent', fontWeight: activeSection === s.id ? '600' : '400' }}>
                {s.title}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, padding: '40px 48px' }}>
            {aupSections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: '48px', scrollMarginTop: '80px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#e2e8f0', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #1e2330' }}>{s.title}</h2>
                {renderContent(s.content)}
              </div>
            ))}
            <div style={{ marginTop: '48px', padding: '24px', background: '#15181f', border: '1px solid #1e2330', borderRadius: '14px', display: 'flex', gap: '16px' }}>
              <span style={{ fontSize: '24px' }}>🚨</span>
              <div>
                <div style={{ color: '#e2e8f0', fontWeight: '600', marginBottom: '4px' }}>Report a violation</div>
                <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '10px' }}>If you've seen behaviour that violates this policy, let us know.</div>
                <a href={`mailto:${companyEmail}?subject=AUP Report`} style={{ color: '#fca5a5', fontSize: '13px', textDecoration: 'none' }}>{companyEmail}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// CookiePolicy.tsx
// ─────────────────────────────────────────────
const cookieSections = [
  {
    id: 'what-are-cookies',
    title: '1. What Are Cookies?',
    content: `Cookies are small text files stored on your device (computer, phone, tablet) by your web browser when you visit a website. They are widely used to make websites work, remember your preferences, and give website owners information about how their site is being used.

We also use similar technologies such as:
• **Local Storage**: data stored in your browser that persists after you close it.
• **Session Storage**: data that clears when you close your browser tab.
• **Pixels**: small transparent images that help us understand how pages are interacted with.

When we say "cookies" in this policy, we mean all these technologies.`,
  },
  {
    id: 'cookies-we-use',
    title: '2. Cookies We Use',
    content: `We group cookies into four categories. You can control which categories (other than Strictly Necessary) are active using our Cookie Settings panel.

**2.1 Strictly Necessary (always active)**
These are essential for the website to function. Without them, you cannot log in, navigate securely, or use core features. No consent is required.
• zimchat_session — your login session token. Expires when you close your browser.
• zimchat_csrf — cross-site request forgery protection. Session cookie.
• zimchat_prefs — remembers your sidebar collapsed/expanded state and theme. 1 year.

**2.2 Analytics (opt-in)**
These help us understand how people use the Platform so we can improve it. All data is anonymised — we cannot identify individual users from analytics data.
• _ga, _ga_* — Google Analytics 4. Tracks pages visited, time on site, and how you arrived. 2 years.
• _gid — Google Analytics daily session identifier. 24 hours.

**2.3 Functional (opt-in)**
These enable enhanced features and personalisation.
• zimchat_onboarding — remembers which onboarding steps you have completed. 30 days.
• zimchat_tutorial — remembers if you have dismissed tutorial prompts. 1 year.

**2.4 Marketing (opt-in)**
These are used to show you relevant advertising and measure ad campaign performance. We use these sparingly — we do not show ads within the Platform itself.
• _fbp — Facebook Pixel. Used on our public marketing pages to measure Facebook ad conversions. 90 days.
• Google Ads conversion cookies — only on our public-facing marketing pages, not within the dashboard.`,
  },
  {
    id: 'third-party',
    title: '3. Third-Party Cookies',
    content: `Some cookies are placed by third-party services embedded in our pages:

• **Google Analytics** (analytics.google.com) — Analytics cookies. Subject to Google's Privacy Policy.
• **Stripe** (stripe.com) — Payment security cookies, fraud detection. Placed on payment pages. Subject to Stripe's Privacy Policy.
• **Facebook** (facebook.com) — Pixel on our marketing pages only. Subject to Meta's Data Policy.

We do not control the cookies set by these third parties. You can opt out using the controls below or by visiting the respective companies' opt-out tools.`,
  },
  {
    id: 'your-choices',
    title: '4. Your Choices',
    content: `**4.1 Our cookie consent tool**
When you first visit the Platform, a cookie consent banner appears. You can accept all cookies, reject optional cookies, or customise your choices by category. You can change your preferences at any time by clicking "Cookie Settings" in our website footer.

**4.2 Browser settings**
All modern browsers let you view, block, and delete cookies. Instructions:
• Chrome: Settings → Privacy and Security → Cookies and other site data
• Firefox: Settings → Privacy & Security → Cookies and Site Data
• Safari: Preferences → Privacy → Manage Website Data
• Edge: Settings → Cookies and site permissions

Note that blocking Strictly Necessary cookies will prevent you from logging in.

**4.3 Opt-out tools**
• Google Analytics: https://tools.google.com/dlpage/gaoptout
• Facebook: https://www.facebook.com/settings/?tab=ads
• Digital Advertising Alliance (opt out of interest-based ads): https://optout.aboutads.info/

**4.4 Do Not Track**
Some browsers send a "Do Not Track" signal. We honour this signal by not loading optional analytics or marketing cookies when it is detected.`,
  },
  {
    id: 'data-from-cookies',
    title: '5. Data Collected via Cookies',
    content: `The analytics and functional cookies we use collect information such as:
• Pages visited and time spent on each page.
• Which features of the dashboard were clicked.
• Your approximate geographic region (country/city level — not your precise location).
• Browser type, device type, and operating system.
• How you arrived at our website (search engine, direct link, referral).

This data is used in aggregate to improve the Platform. It is not used to build personal profiles for advertising, and it is not sold to third parties.`,
  },
  {
    id: 'updates',
    title: '6. Updates to This Policy',
    content: `We may update this Cookie Policy when we add or remove cookies or when laws change. We will update the "Last updated" date and, where changes are significant, notify you via the cookie consent banner.`,
  },
];

export function CookiePolicy() {
  const [activeSection, setActiveSection] = useState('what-are-cookies');
  return (
    <>
      <SEO title={`Cookie Policy | ${companyName}`} description={`How ${companyName} uses cookies and similar technologies.`} keywords="cookie policy, cookies, tracking" type="website" />
      <style>{policyStyles}</style>
      <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif", background: '#0f1117', color: 'white', minHeight: '100vh' }}>
        <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: '60px', background: 'rgba(15,17,23,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1e2330', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 32px' }}>
          <Link to="/"><img src="/Logo-white.png" alt={companyName} style={{ height: '68px', display: 'block', objectFit: 'contain' }} /></Link>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Link to="/privacy" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Privacy</Link>
            <Link to="/terms" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Terms</Link>
            <Link to="/acceptable-use" style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px' }}>Acceptable Use</Link>
          </div>
        </nav>
        <div style={{ paddingTop: '100px', paddingBottom: '40px', paddingLeft: '32px', paddingRight: '32px', borderBottom: '1px solid #1e2330' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'inline-block', background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', letterSpacing: '0.08em', marginBottom: '16px' }}>LEGAL</div>
            <h1 style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '8px' }}>Cookie Policy</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Last updated: {lastUpdated}</p>
          </div>
        </div>
        <div className="policy-layout" style={{ display: 'flex', maxWidth: '1100px', margin: '0 auto' }}>
          <div className="policy-sidebar" style={{ width: '260px', flexShrink: 0, borderRight: '1px solid #1e2330', padding: '24px 0', position: 'sticky', top: '60px', height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            <div style={{ padding: '0 16px', marginBottom: '12px', fontSize: '10px', fontWeight: '700', color: '#334155', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Sections</div>
            {cookieSections.map(s => (
              <div key={s.id} className={`policy-nav-item ${activeSection === s.id ? 'active' : ''}`} onClick={() => { setActiveSection(s.id); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                style={{ padding: '9px 20px', fontSize: '13px', color: activeSection === s.id ? '#6ee7b7' : '#64748b', borderLeft: activeSection === s.id ? '3px solid #10b981' : '3px solid transparent', fontWeight: activeSection === s.id ? '600' : '400' }}>
                {s.title}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, padding: '40px 48px' }}>
            {cookieSections.map(s => (
              <div key={s.id} id={s.id} style={{ marginBottom: '48px', scrollMarginTop: '80px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#e2e8f0', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #1e2330' }}>{s.title}</h2>
                {renderContent(s.content)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default AcceptableUsePolicy;
